// ===== Item Transfer UI (Phase 5, T6 bigtionax-chat-item-transfer) =====
// 전달 진입점 2종이 공유하는 room picker: 아티팩트 패널 '다른 방에 전달'(artifact-panel.js가
// openPicker(anchorEl, { artifact })로 호출)과 컴포저 plus-menu '대화요약 전달'(이 파일이 직접
// 배선). 리스트 데이터·바깥클릭 닫기·in-flight disabled·실패 시 유지 관례는 artifact-panel.js의
// send-to-plugin 팝오버 배선(_renderActionsPopover/_onActionsOutsideClick)을 그대로 미러한다.
//
// currentRoomId SSOT 불변식: RoomList.getRoomIdForSession(currentSessionId)을 읽기 전용으로만
// 소비한다 — resumeSession/setCurrentRoomId 배선에는 손대지 않는다.

interface ItemTransferRoom { id: string; title?: string | null; sessionId?: string | null }

type ItemTransferTarget =
  | { kind: "artifact"; artifactId: string }
  | { kind: "summary"; sourceRoomId: string };

interface Window {
  ItemTransfer?: { openPicker: (anchorEl: HTMLElement, ctx: unknown) => void; closePicker: () => void };
}

(function () {
  var _pickerEl: HTMLElement | null = null;
  var _anchorEl: HTMLElement | null = null;

  function _isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null;
  }

  function _isTransferRoom(value: unknown): value is ItemTransferRoom {
    return _isRecord(value) && typeof value.id === "string"
      && (!("title" in value) || value.title == null || typeof value.title === "string")
      && (!("sessionId" in value) || value.sessionId == null || typeof value.sessionId === "string");
  }

  function _errorMessage(error: unknown): string {
    return error instanceof Error ? error.message : String(error);
  }

  // ctx = { artifact } (패널 leg) | { summary: true, sourceRoomId } (컴포저 leg) — 원본을 신뢰하지
  // 않고 형태를 검증한 뒤 내부 표현으로 정규화한다(ArtifactPanel의 isArtifactPanelArtifact 관례 미러).
  function _parseCtx(ctx: unknown): ItemTransferTarget | null {
    if (!_isRecord(ctx)) return null;
    if (ctx.summary === true) {
      var sourceRoomId = ctx.sourceRoomId;
      return typeof sourceRoomId === "string" && sourceRoomId ? { kind: "summary", sourceRoomId: sourceRoomId } : null;
    }
    var artifact = ctx.artifact;
    if (_isRecord(artifact) && typeof artifact.id === "string") {
      return { kind: "artifact", artifactId: artifact.id };
    }
    return null;
  }

  function _mountParent(target: ItemTransferTarget): HTMLElement | null {
    if (target.kind === "summary") return document.querySelector<HTMLElement>(".input-wrapper");
    return document.getElementById("artifact-detail-sidepanel-actions");
  }

  // 소스 룸: 컴포저 leg는 호출부가 넘긴 값을 그대로 쓰고(plus-menu 열림 시점 계산), 패널 leg는
  // 지금 보고 있는 방(RoomList.getRoomIdForSession(currentSessionId))을 읽기 전용으로 파생한다.
  function _sourceRoomId(target: ItemTransferTarget): string | null {
    if (target.kind === "summary") return target.sourceRoomId;
    if (typeof RoomList === "undefined") return null;
    var sessionId = window.currentSessionId;
    if (typeof sessionId !== "string" || !sessionId) return null;
    return RoomList.getRoomIdForSession(sessionId);
  }

  function _transferUrl(target: ItemTransferTarget): string {
    return target.kind === "summary"
      ? "/api/artifacts/summary-transfer"
      : "/api/artifacts/" + encodeURIComponent(target.artifactId) + "/transfer";
  }

  function _transferBody(target: ItemTransferTarget, targetRoomId: string, sourceRoomId: string | null): Record<string, unknown> {
    if (target.kind === "summary") {
      return { sourceRoomId: target.sourceRoomId, targetRoomId: targetRoomId };
    }
    var body: Record<string, unknown> = { targetRoomId: targetRoomId };
    if (sourceRoomId) body.sourceRoomId = sourceRoomId;
    return body;
  }

  async function _itFetchJson(url: string, options?: RequestInit): Promise<Record<string, unknown>> {
    var response = await fetch(url, options);
    var payload: unknown = await response.json().catch(function () { return {}; });
    if (!_isRecord(payload)) throw new Error("Invalid JSON response");
    if (!response.ok || payload.ok === false) {
      throw new Error(typeof payload.error === "string" ? payload.error : "Request failed: " + response.status);
    }
    return payload;
  }

  async function _fetchRooms(): Promise<ItemTransferRoom[]> {
    try {
      var response = await fetch("/api/rooms");
      if (!response.ok) return [];
      var data: unknown = await response.json().catch(function () { return {}; });
      if (!_isRecord(data) || data.ok !== true || !Array.isArray(data.rooms)) return [];
      return data.rooms.filter(_isTransferRoom);
    } catch (error) {
      return [];
    }
  }

  function _onOutsideClick(event: MouseEvent): void {
    if (_pickerEl && event.target instanceof Node && _pickerEl.contains(event.target)) return;
    if (_anchorEl && event.target instanceof Node && _anchorEl.contains(event.target)) return;
    closePicker();
  }

  function closePicker(): void {
    if (_pickerEl) _pickerEl.remove();
    _pickerEl = null;
    // 성공 경로의 in-flight 잠금 해제 지점 — 앵커는 index.html 고정 엘리먼트라(#artifact-btn-transfer)
    // disabled가 남으면 패널을 닫았다 다시 열어도 두 번째 전달이 불가능해진다(실패 경로는
    // _setPickerLoading(false)가 이미 푼다). picker가 열렸다는 것 자체가 앵커가 활성이었다는 뜻이라
    // 여기서 푸는 게 안전하다 — 컴포저 leg의 비룸 세션 비활성은 plus-menu 열림마다 재계산된다.
    if (_anchorEl) _anchorEl.removeAttribute("disabled");
    _anchorEl = null;
    document.removeEventListener("click", _onOutsideClick, true);
  }

  // 연타 억제(AR2 클라 완화): in-flight 동안 목록 항목 + 트리거 버튼을 함께 disabled 처리한다.
  function _setPickerLoading(loading: boolean): void {
    if (_pickerEl) {
      _pickerEl.querySelectorAll<HTMLButtonElement>(".item-transfer-picker-item").forEach(function (btn) {
        btn.toggleAttribute("disabled", loading);
      });
    }
    if (_anchorEl) _anchorEl.toggleAttribute("disabled", loading);
  }

  // 실패해도 picker를 닫지 않는다 — 같은 항목을 다시 눌러 재시도할 수 있게 둔다
  // (artifact-panel.js _sendToPlugin의 재시도 계약 미러).
  async function _onItemClick(target: ItemTransferTarget, targetRoomId: string, sourceRoomId: string | null): Promise<void> {
    _setPickerLoading(true);
    try {
      await _itFetchJson(_transferUrl(target), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(_transferBody(target, targetRoomId, sourceRoomId)),
      });
      closePicker();
      if (typeof showSummaryToast === "function") showSummaryToast("다른 방으로 전달했어요", true);
    } catch (error) {
      _setPickerLoading(false);
      if (typeof showSummaryToast === "function") showSummaryToast("전달에 실패했어요: " + _errorMessage(error) + " — 다시 시도해 주세요", true);
    }
  }

  function _renderPicker(parent: HTMLElement, target: ItemTransferTarget, rooms: ItemTransferRoom[], sourceRoomId: string | null): void {
    var pop = document.createElement("div");
    pop.className = "item-transfer-picker";

    if (!rooms.length) {
      // .artifact-panel-actions-empty는 artifact-panel.css에 정의되어 있지만 전역 스타일시트라
      // 컴포저 leg에서도 그대로 재사용 가능하다(index.html에 두 CSS 모두 무조건 로드).
      pop.innerHTML = '<div class="artifact-panel-actions-empty">전달할 수 있는 다른 방이 없습니다</div>';
    } else {
      pop.innerHTML = rooms.map(function (room) {
        var label = room.title || "이름 없는 방";
        return '<button type="button" class="item-transfer-picker-item" data-room-id="' + escapeHtml(room.id) + '">' +
          escapeHtml(label) + '</button>';
      }).join("");
      pop.querySelectorAll<HTMLButtonElement>(".item-transfer-picker-item").forEach(function (btn) {
        btn.addEventListener("click", function () {
          var roomId = btn.dataset.roomId;
          if (roomId) _onItemClick(target, roomId, sourceRoomId);
        });
      });
    }

    parent.appendChild(pop);
    _pickerEl = pop;
    setTimeout(function () {
      document.addEventListener("click", _onOutsideClick, true);
    }, 0);
  }

  async function openPicker(anchorEl: HTMLElement, ctx: unknown): Promise<void> {
    closePicker(); // 재진입 안전
    var target = _parseCtx(ctx);
    if (!target) return;
    var parent = _mountParent(target);
    if (!parent) return;

    _anchorEl = anchorEl;
    var sourceRoomId = _sourceRoomId(target);
    var rooms = await _fetchRooms();
    // 현재 방 제외 + sessionId 있는 방만(세션 없는 방은 전달 대상 로그가 없어 배달 불가).
    // lastMessagePreview는 렌더하지 않는다(PII 파리티).
    var candidates = rooms.filter(function (room) {
      return !!room.sessionId && room.id !== sourceRoomId;
    });

    _renderPicker(parent, target, candidates, sourceRoomId);
  }

  window.ItemTransfer = { openPicker: openPicker, closePicker: closePicker };

  // ===== 컴포저 leg: plus-menu '대화요약 전달' 배선 =====
  var summaryTransferBtn = document.getElementById("summaryTransferBtn");
  var plusBtn = document.getElementById("plusBtn");

  // 비룸 세션(1:1 하나 대화 등)에서는 전달할 소스 룸이 없어 항목을 비활성화한다.
  function _recalcSummaryTransferState(): void {
    if (!summaryTransferBtn) return;
    var sessionId = window.currentSessionId;
    var hasRoom = typeof RoomList !== "undefined" && typeof sessionId === "string" && !!sessionId
      && !!RoomList.getRoomIdForSession(sessionId);
    summaryTransferBtn.toggleAttribute("disabled", !hasRoom);
  }

  function _onSummaryTransferClick(): void {
    // attachBtn 선례(file-attachment.js) 미러 — 항목 클릭 시 plusMenu show/active를 명시적으로 지운다.
    var plusMenu = document.getElementById("plusMenu");
    plusMenu?.classList.remove("show");
    plusBtn?.classList.remove("active");
    if (!summaryTransferBtn || summaryTransferBtn.hasAttribute("disabled")) return;
    var sessionId = window.currentSessionId;
    if (typeof RoomList === "undefined" || typeof sessionId !== "string" || !sessionId) return;
    var sourceRoomId = RoomList.getRoomIdForSession(sessionId);
    if (!sourceRoomId) return;
    openPicker(summaryTransferBtn, { summary: true, sourceRoomId: sourceRoomId });
  }

  if (plusBtn) plusBtn.addEventListener("click", _recalcSummaryTransferState);
  if (summaryTransferBtn) summaryTransferBtn.addEventListener("click", _onSummaryTransferClick);
})();
